import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Select } from 'antd';

import { CardPickerOptionRow } from '../cards/CardVisuals';
import { optionSearchFilter } from '../Input';
import { SEARCH_GLASS } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/** The value of the first row, which clears the card filter. */
export const ALL_CARDS = '__all-cards__';

/** The longest card name a row shows whole (KPR-1634); a longer one ellipsizes. */
export const CARD_NAME_ROW_CHARS = 32;

const KEY_CODES = { ArrowDown: 40, ArrowUp: 38, Enter: 13 };

/**
 * Narrows picker options, flat or grouped, to those whose searchText holds the
 * needle (case-insensitively). A group is kept, headed as before, only while
 * one of its options matches.
 */
export function filterCardPickerOptions(options = [], needle = '') {
	if (!`${needle}`.trim()) return options;
	return options.flatMap(option => {
		if (Array.isArray(option?.options)) {
			const kept = option.options.filter(child => optionSearchFilter(needle, child));
			return kept.length ? [{ ...option, options: kept }] : [];
		}
		return optionSearchFilter(needle, option) ? [option] : [];
	});
}

const ALL_CARDS_OPTION = {
	value: ALL_CARDS,
	searchText: 'All cards',
	label: <span className="card-picker-all">All cards</span>,
	isAll: true,
};

/**
 * The items page's card filter (KPR-1634). A Select whose menu has room: a
 * search box at its top, an All cards row, a Pinned group, then the rest —
 * child cards indented under their parent, by the options' own depth.
 *
 * Typing happens in the menu's box, not in the trigger; the list keys
 * (↑ ↓ Enter) are forwarded from that box to the Select's own list, so there
 * is one keyboard model. Escape closes the menu and returns focus to the
 * trigger, and so does picking a row.
 */
export default function CardFilterSelect({
	options,
	value,
	onChange,
	collectionId,
	collectionFavoriteIds,
	className = 'items-page__card-filter',
}) {
	const selectRef = useRef(null);
	const searchRef = useRef(null);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');

	const shown = useMemo(
		() => [ALL_CARDS_OPTION, ...filterCardPickerOptions(options, query)]
			// The All cards row answers to its own name, but is not hidden by a
			// search for another: it would be the only row left over.
			.filter(option => !option.isAll || !query.trim() || optionSearchFilter(query, option)),
		[options, query]
	);

	const focusTrigger = useCallback(() => {
		selectRef.current?.focus?.();
	}, []);

	const changeOpen = useCallback(next => {
		setOpen(next);
		if (!next) setQuery('');
	}, []);

	// Opening puts the cursor in the menu's search box. A touch screen is left
	// alone: its keyboard would cover the list it is meant to search.
	useEffect(() => {
		if (!open) return undefined;
		const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches;
		if (coarse) return undefined;
		const timer = setTimeout(() => searchRef.current?.focus?.(), 0);
		return () => clearTimeout(timer);
	}, [open]);

	const forwardKey = event => {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			changeOpen(false);
			focusTrigger();
			return;
		}
		if (!KEY_CODES[event.key]) return;
		event.preventDefault();
		const target = selectRef.current?.nativeElement?.querySelector?.('input');
		if (!target) return;
		const init = {
			key: event.key,
			code: event.key,
			keyCode: KEY_CODES[event.key],
			which: KEY_CODES[event.key],
			bubbles: true,
			cancelable: true,
		};
		target.dispatchEvent(new KeyboardEvent('keydown', init));
		target.dispatchEvent(new KeyboardEvent('keyup', init));
	};

	return (
		<Select
			ref={selectRef}
			className={className}
			aria-label="Card"
			value={value || ALL_CARDS}
			onChange={next => {
				onChange(next === ALL_CARDS ? undefined : next);
				changeOpen(false);
				focusTrigger();
			}}
			options={shown}
			open={open}
			onOpenChange={changeOpen}
			// The menu keeps its own width, 320–420px, whatever the trigger's
			// (items-list.css); the search lives in the menu, below.
			popupMatchSelectWidth={false}
			classNames={{ popup: { root: 'card-picker-dropdown card-picker-dropdown--wide' } }}
			showSearch={false}
			filterOption={false}
			searchValue={query}
			notFoundContent={<div className="card-picker-empty">No card matches that name</div>}
			popupRender={menu => (
				<>
					<div className="card-picker-search">
						<Input
							{...NO_TEXT_ASSIST}
							ref={searchRef}
							size="middle"
							allowClear
							prefix={<SEARCH_GLASS aria-hidden="true" />}
							placeholder="Search cards"
							aria-label="Search cards"
							value={query}
							onChange={event => setQuery(event.target.value)}
							onKeyDown={forwardKey}
							onMouseDown={event => event.stopPropagation()}
						/>
					</div>
					{menu}
				</>
			)}
			// A borrowed card is nobody's pin here: its row is its label,
			// "from <collection>" included.
			optionRender={option => {
				if (option?.data?.isAll || option?.data?.linked) return option.data.label;
				return (
					<CardPickerOptionRow
						option={option}
						collectionId={collectionId}
						collectionFavoriteIds={collectionFavoriteIds}
					/>
				);
			}}
		/>
	);
}
